<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :title="getModalTitle()"
    :hide-save-button="!modifiedData.isFromManager || viewOnly"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div v-if="representative" class="pointer-events-none flex flex-col">
        <label for="representative" class="text-sm font-medium mb-1">
          {{ $t('representative.representative') }} - {{ representative.name }}
        </label>
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="remoteCode" class="text-sm font-medium">{{
          $t('transactions.remoteCode')
        }}</label>
        <BaseInput
          v-model="modifiedData.warehouseRemotedCode"
          color="base"
          :disabled="mode === 'assign' || viewOnly"
          show-error-as-tooltip
          :error="
            $v.warehouseRemotedCode.required.$invalid
              ? $v.warehouseRemotedCode.required.$message
              : $v.warehouseRemotedCode.uniqueRemoteCode.$invalid
                ? $v.warehouseRemotedCode.uniqueRemoteCode.$message
                : ''
          " />
      </div>

      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }">
        <label for="warehouse" class="text-sm font-medium">{{
          $t('representative.companyWarehouse')
        }}</label>
        <BaseSearchDropdown
          :list-items="dropdownWarehouses"
          v-model="selectedWarehouse"
          :no-results-text="$t('messages.noResultsText')"
          name="warehouse"
          show-error-as-tooltip
          internal-filter
          :disabled="
            !modifiedData.isFromManager ||
            viewOnly ||
            (mode === 'assign' && !!warehouse.warehouseId)
          "
          :error="$v.warehouseId.required.$invalid ? $v.warehouseId.required.$message : ''"
          @item-selected="handleWarehouseSelection"
          @clear-selection="handleWarehouseSelection" />
      </div>

      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager || viewOnly }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        :disable="mode === 'assign'"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSearchDropdown from '@/components/common/BaseSearchDropdown.vue';
import BaseToggle from '../common/BaseToggle.vue';

import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import type { DCWarehouse } from '@/types/DataCollection';
import type { Representative } from '@/types/Traders';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit' | 'assign' | 'view';
    warehouse: Partial<DCWarehouse>;
    representativeId: string;
    representative?: Partial<Representative>;
    existingWarehouses?: DCWarehouse[];
    viewOnly?: boolean;
  }>(),
  {}
);

const { getWarehousesLookup } = useLookupsStore();
const { warehouses: lookupWarehouses, dropdownWarehouses } = storeToRefs(useLookupsStore());

const modifiedData = ref<Partial<DCWarehouse>>(deepCopy<Partial<DCWarehouse>>(props.warehouse));

const isModified = ref<boolean>(false);

const validationModel = ref({
  warehouseId: modifiedData.value.warehouseId || '',
  warehouseRemotedCode: modifiedData.value.warehouseRemotedCode || '',
});

// Custom validator for uniqueness
const isUniqueRemoteCode = (value: string) => {
  if (!value || !props.existingWarehouses) return true;

  const existingDuplicate = props.existingWarehouses.find(
    (warehouse) =>
      warehouse.id !== modifiedData.value.id &&
      warehouse.warehouseRemotedCode === value &&
      warehouse.recordMode !== RecordMode.Deleted
  );

  return !existingDuplicate;
};

const rules = computed(() => ({
  warehouseId: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  warehouseRemotedCode: {
    required: helpers.withMessage($t('messages.errors.required'), required),
    uniqueRemoteCode: helpers.withMessage(
      $t('messages.errors.duplicateRemoteCode'),
      isUniqueRemoteCode
    ),
  },
}));

const $v = useVuelidate(rules, validationModel);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.warehouseId = newData.warehouseId || '';
    validationModel.value.warehouseRemotedCode = newData.warehouseRemotedCode || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.warehouse);
  },
  { deep: true }
);

const selectedWarehouse = ref<SelectDropdownItem | null>(null);

function onDismissChanges() {
  modifiedData.value = deepCopy<Partial<DCWarehouse>>(props.warehouse);
  emit('closeModal');
}

function onSave() {
  $v.value.$validate();
  if ($v.value.$invalid) return;
  modifiedData.value.representativeId = props.representativeId;
  if (modifiedData.value.recordMode !== RecordMode.Added)
    modifiedData.value.recordMode = props.mode === 'add' ? RecordMode.Added : RecordMode.Modified;
  emit('save', modifiedData.value);
}

function handleWarehouseSelection(item: SelectDropdownItem) {
  selectedWarehouse.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.warehouseId = item ? item.value.toString() : '';
    const warehouse = lookupWarehouses.value.find((x) => x.id == item.value.toString());
    if (warehouse) modifiedData.value.warehouse = warehouse;
  }
}

onMounted(async () => {
  await getWarehousesLookup();

  if (modifiedData.value && modifiedData.value.warehouseId) {
    const foundType = lookupWarehouses.value.find((t) => t.id === modifiedData.value.warehouseId);
    if (foundType) {
      selectedWarehouse.value = {
        name: foundType.name as string,
        value: foundType.id,
      };
    }
  }
});

function getModalTitle() {
  const actionKey =
    props.viewOnly || props.mode === 'view' ? 'view' : props.mode === 'add' ? 'create' : 'edit';
  return `${$t(`common.actions.${actionKey}`)} ${$t('representative.entities.warehouse')}`;
}
</script>
